import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '@/shared/api'
import type {
  LLMsListItem,
  SelectedLLM,
  CreateLlmInput,
  CreateLLMSuccessResponse,
  DeleteLLMSuccessResponse,
  UpdateLLMInput,
  UpdateLLMSuccessResponse,
  DuplicateLlmInput,
  DuplicateLLMSuccessResponse,
} from '@intone/shared';

const LLMS_LIST_KEY = ['llms-list'];
const LLM_DETAILS_KEY = (id: number | null) => ['llms', id];

/** Fetch list of LLMs */
export function useLLMsList() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: LLMS_LIST_KEY,
    queryFn: async () => apiClient<LLMsListItem[]>('/llms'),
  })
}

/** Fetch a single LLM by ID */
export function useLLM(id: number | null) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: LLM_DETAILS_KEY(id),
    queryFn: async () => apiClient<SelectedLLM>(`/llms/${id}`),
    enabled: !!id,
    initialData: () => {
      return queryClient.getQueryData(LLM_DETAILS_KEY(id));
    },
  })
}

/** Create a new LLM */
export function useCreateLLM() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (script: CreateLlmInput) => {
      return apiClient<CreateLLMSuccessResponse>('/llms', {
        method: 'POST',
        body: JSON.stringify(script),
      })
    },
    onMutate: async (llm: CreateLlmInput) => {
      await queryClient.cancelQueries({ queryKey: LLMS_LIST_KEY });
      const prevList = queryClient.getQueryData(LLMS_LIST_KEY);

      queryClient.setQueryData(LLMS_LIST_KEY, (old: LLMsListItem[] = []) => [
        {...llm, id: 0 },
        ...old,
      ]);
      return { prevList };
    },
    onError: (_, __, context) => {
      if (context) {
        queryClient.setQueryData(LLMS_LIST_KEY, context.prevList);
      }
    },
    onSuccess: ({ llm }) => {
      queryClient.setQueryData(LLMS_LIST_KEY, (old: LLMsListItem[] = []) =>
        old.map((item) => item.id === 0 ? { ...item, id: llm.id } : item)
      );
      queryClient.setQueryData(LLM_DETAILS_KEY(llm.id), llm);
    },
  })
}

/** Update an existing script */
export function useUpdateLLM() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (llm: UpdateLLMInput) => {
      return apiClient<UpdateLLMSuccessResponse>(`/llms/${llm.id}`, {
        method: 'PUT',
        body: JSON.stringify(llm),
      })
    },
    onMutate: async (llm: UpdateLLMInput) => {
      await queryClient.cancelQueries({ queryKey: LLM_DETAILS_KEY(llm.id) });
      await queryClient.cancelQueries({ queryKey: LLMS_LIST_KEY });

      const prevLLM = queryClient.getQueryData(LLM_DETAILS_KEY(llm.id));
      const prevList = queryClient.getQueryData(LLMS_LIST_KEY);

      queryClient.setQueryData(LLM_DETAILS_KEY(llm.id), (old: SelectedLLM) => ({ ...old, ...llm }));

      queryClient.setQueryData(LLMS_LIST_KEY, (old: LLMsListItem[] = []) =>
        old.map((item) => item.id === llm.id ? { ...item, ...llm } : item)
      );

      return { prevList, prevLLM };
    },
    onError: (_, oldLLm, context) => {
      if (context) {
        queryClient.setQueryData(LLMS_LIST_KEY, context.prevList);
        queryClient.setQueryData(LLM_DETAILS_KEY(oldLLm.id), context.prevLLM);
      }
    },
    onSuccess: ({ llm }) => {
      queryClient.setQueryData(LLM_DETAILS_KEY(llm.id), llm);
      queryClient.setQueryData(LLMS_LIST_KEY, (old: LLMsListItem[] = []) =>
        old.map((item) => item.id === llm.id ? {
          ...item,
          name: llm.name,
          model: llm.model,
        } : item)
      );
    },
  })
}

/** Delete a LLM */
export function useDeleteLLM() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      return apiClient<DeleteLLMSuccessResponse>(`/llms/${id}`, {
        method: 'DELETE',
      })
    },
    onMutate: async (deletedId) => {
      await queryClient.cancelQueries({ queryKey: LLMS_LIST_KEY })
      const prevList = queryClient.getQueryData(LLMS_LIST_KEY);
      queryClient.setQueryData(LLMS_LIST_KEY, (old: LLMsListItem[] = []) =>
        (old.filter((item) => item.id !== deletedId))
      );
      return { prevList };
    },
    onError: (_, __, context) => {
      if (context) {
        queryClient.setQueryData(LLMS_LIST_KEY, context.prevList);
      }
    },
    onSuccess: ({ LLMId }) => {
      queryClient.removeQueries({queryKey: LLM_DETAILS_KEY(LLMId)})
    },
  })
}

/** Duplicate a LLM */
export function useDuplicateLLM() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ llmId, newName }: DuplicateLlmInput) => {
      return apiClient<DuplicateLLMSuccessResponse>(`/llms/${llmId}/duplicate`, {
        method: 'POST',
        body: JSON.stringify({ newName }),
      })
    },
    onMutate: async (llm: DuplicateLlmInput) => {
      await queryClient.cancelQueries({ queryKey: LLMS_LIST_KEY });
      const prevList = queryClient.getQueryData(LLMS_LIST_KEY);

      queryClient.setQueryData(LLMS_LIST_KEY, (old: LLMsListItem[] = []) => [
        {...llm, id: 0, name: llm.newName },
        ...old,
      ]);
      return { prevList };
    },
    onError: (_, __, context) => {
      if (context) {
        queryClient.setQueryData(LLMS_LIST_KEY, context.prevList);
      }
    },
    onSuccess: ({ llm }) => {
      queryClient.setQueryData(LLMS_LIST_KEY, (old: LLMsListItem[] = []) =>
        old.map((item) => item.id === 0 ? { ...item, id: llm.id } : item)
      );
      queryClient.setQueryData(LLM_DETAILS_KEY(llm.id), llm);
    },
  })
}